import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { PlusIcon, TrashIcon, Squares2X2Icon, ListBulletIcon } from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import {
  Button,
  Spinner,
  Alert,
  Checkbox,
  ConfirmationDialog,
  Input,
} from '@/components/ui';
import { ViewToggle } from '@/components/ui/ViewToggle';
import { cn } from '@/utils/cn';
import { TemplateCard } from '@/components/template';
import { TemplateListView } from '@/components/template/TemplateListView';
import {
  useTemplates,
  useDeleteTemplate,
  useBulkDeleteTemplates,
  useDeleteAllTemplates,
} from '@/hooks';

const TEMPLATE_VIEW_KEY = 'template_view_mode';

function TemplateLibrary() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // D12: Active vs Archived view. Archived templates are fetched only on demand.
  const [showArchived, setShowArchived] = useState(false);
  const { data: templates, isLoading } = useTemplates(showArchived);
  const canArchive = user?.role === 'coach' || user?.role === 'admin';
  const { mutate: deleteTemplate } = useDeleteTemplate();
  const bulkDelete = useBulkDeleteTemplates();
  const deleteAll = useDeleteAllTemplates();

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // View mode: gallery or list, persisted in localStorage
  const [viewMode, setViewMode] = useState<'gallery' | 'list'>(() => {
    if (typeof window === 'undefined') return 'gallery';
    return (localStorage.getItem(TEMPLATE_VIEW_KEY) as 'gallery' | 'list') ?? 'gallery';
  });

  useEffect(() => {
    localStorage.setItem(TEMPLATE_VIEW_KEY, viewMode);
  }, [viewMode]);

  // Sort state for list view
  const [sortField, setSortField] = useState<'name' | 'created_at'>('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const handleSort = (field: 'name' | 'created_at') => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir(field === 'created_at' ? 'desc' : 'asc');
    }
  };

  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Modal state
  const [showDeleteSelectedModal, setShowDeleteSelectedModal] = useState(false);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<string | null>(null);
  const [deleteResult, setDeleteResult] = useState<{
    type: 'success' | 'error' | 'partial';
    message: string;
  } | null>(null);

  // Sort: apply sortField + sortDir
  const sortedTemplates = useMemo(() => {
    if (!templates) return [];
    return [...templates].sort((a, b) => {
      let cmp = 0;
      if (sortField === 'name') {
        cmp = a.name.localeCompare(b.name);
      } else {
        cmp = new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [templates, sortField, sortDir]);

  // Filter: archive-tab scope + case-insensitive name substring match.
  // The Active tab fetches with include_archived=false (backend already excludes
  // archived). The Archived tab fetches everything, so narrow to archived here.
  const displayedTemplates = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return sortedTemplates.filter((t) => {
      if (showArchived && !t.is_archived) return false;
      if (q && !t.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [sortedTemplates, searchQuery, showArchived]);

  // Computed selection state — scoped to the visible (filtered) set
  const templateIds = useMemo(
    () => displayedTemplates.map((t) => t.id),
    [displayedTemplates]
  );

  const allSelected = templateIds.length > 0 && selectedIds.size === templateIds.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < templateIds.length;
  const noneSelected = selectedIds.size === 0;

  // Selection handlers
  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(templateIds));
    }
  };

  const handleSelectOne = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  // Delete handlers — single delete now uses ConfirmationDialog (not window.confirm)
  const handleDeleteTemplate = (templateId: string) => {
    setTemplateToDelete(templateId);
  };

  const confirmDeleteTemplate = () => {
    if (templateToDelete) {
      deleteTemplate(templateToDelete);
      setTemplateToDelete(null);
    }
  };

  const handleEvaluateTemplate = (templateId: string) => {
    navigate(`/evaluate/${templateId}`);
  };

  const handleDeleteSelected = () => {
    bulkDelete.mutate(Array.from(selectedIds), {
      onSuccess: (result) => {
        setShowDeleteSelectedModal(false);

        if (result.failed_count === 0) {
          setSelectedIds(new Set());
          setDeleteResult({
            type: 'success',
            message: `Successfully deleted ${result.deleted_count} template(s).`,
          });
        } else if (result.deleted_count === 0) {
          // All failed — keep selection so user can retry
          setDeleteResult({
            type: 'error',
            message: `Failed to delete all ${result.failed_count} template(s).`,
          });
        } else {
          // Partial failure — keep only the failed IDs selected
          setSelectedIds(new Set(result.failed_ids ?? []));
          setDeleteResult({
            type: 'partial',
            message: `Deleted ${result.deleted_count} template(s). ${result.failed_count} failed.`,
          });
        }
      },
      onError: (error) => {
        setShowDeleteSelectedModal(false);
        setDeleteResult({
          type: 'error',
          message: `Delete failed: ${error.message}`,
        });
      },
    });
  };

  const handleDeleteAll = () => {
    deleteAll.mutate(undefined, {
      onSuccess: (result) => {
        setSelectedIds(new Set());
        setShowDeleteAllModal(false);
        setDeleteResult({
          type: 'success',
          message: result.message,
        });
      },
      onError: (error) => {
        setShowDeleteAllModal(false);
        setDeleteResult({
          type: 'error',
          message: `Delete all failed: ${error.message}`,
        });
      },
    });
  };

  return (
    <div>
      <PageHeader
        title="Templates"
        description="Create and manage your movement templates for athlete evaluations"
        action={
          <Button
            onClick={() => navigate('/templates/create')}
            className="bg-accent hover:bg-accent-hover text-dark-base font-semibold"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Open Coaching Studio
          </Button>
        }
      />

      {/* Result alert */}
      {deleteResult && (
        <Alert
          variant={
            deleteResult.type === 'success'
              ? 'success'
              : deleteResult.type === 'error'
              ? 'error'
              : 'warning'
          }
          onClose={() => setDeleteResult(null)}
          className="mb-4"
        >
          {deleteResult.message}
        </Alert>
      )}

      {/* Active / Archived tabs (D12) — coach/admin only */}
      {canArchive && (
        <div className="inline-flex rounded-lg border border-border-subtle bg-dark-elevated-1 p-1 mb-4">
          {([
            { value: false, label: 'Active' },
            { value: true, label: 'Archived' },
          ] as const).map((tab) => (
            <button
              key={tab.label}
              onClick={() => {
                setShowArchived(tab.value);
                setSelectedIds(new Set());
              }}
              className={cn(
                'px-4 py-1.5 text-sm rounded-md transition-colors',
                showArchived === tab.value
                  ? 'bg-accent text-dark-base font-semibold'
                  : 'text-text-muted hover:text-text-secondary',
              )}
              aria-pressed={showArchived === tab.value}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" label="Loading templates..." />
        </div>
      ) : templates && templates.length > 0 ? (
        <>
          {/* Search bar + view toggle */}
          <div className="flex items-center gap-3 mb-3">
            <div className="flex-grow">
              <Input
                type="text"
                placeholder="Search templates by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <ViewToggle
              modes={[
                { value: 'gallery', icon: Squares2X2Icon, label: 'Gallery view' },
                { value: 'list', icon: ListBulletIcon, label: 'List view' },
              ]}
              value={viewMode}
              onChange={setViewMode}
            />
          </div>

          {/* Bulk actions toolbar */}
          <div className="flex items-center justify-between bg-dark-elevated-1 rounded-lg p-4 mb-4 border border-border-subtle">
            <div className="flex items-center gap-4">
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                onChange={handleSelectAll}
                label={allSelected ? 'Deselect all' : 'Select all'}
              />
              {selectedIds.size > 0 && (
                <span className="text-sm text-text-secondary">
                  {selectedIds.size} selected
                </span>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDeleteSelectedModal(true)}
                disabled={noneSelected}
              >
                <TrashIcon className="h-4 w-4 mr-1" />
                Delete Selected
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDeleteAllModal(true)}
                className="text-danger border-danger hover:bg-dark-elevated-2"
              >
                <TrashIcon className="h-4 w-4 mr-1" />
                Delete All
              </Button>
            </div>
          </div>

          {/* Template cards or empty state (search-empty or archived-empty) */}
          {displayedTemplates.length === 0 ? (
            <div className="text-center py-12">
              {searchQuery ? (
                <>
                  <p className="text-text-muted mb-3">
                    No templates match &ldquo;{searchQuery}&rdquo;
                  </p>
                  <button
                    onClick={() => setSearchQuery('')}
                    className="text-sm text-accent hover:underline"
                  >
                    Clear search
                  </button>
                </>
              ) : (
                <p className="text-text-muted">
                  {showArchived
                    ? 'No archived templates.'
                    : 'No active templates.'}
                </p>
              )}
            </div>
          ) : (
            <>
              {/* List view — desktop only; falls back to gallery on mobile */}
              {viewMode === 'list' && (
                <div className="hidden md:block">
                  <TemplateListView
                    templates={displayedTemplates}
                    selectedIds={selectedIds}
                    onToggleSelect={handleSelectOne}
                    onSelect={(t) => navigate(`/templates/${t.id}`)}
                    onDelete={handleDeleteTemplate}
                    onEvaluate={handleEvaluateTemplate}
                    sortField={sortField}
                    sortDir={sortDir}
                    onSort={handleSort}
                  />
                </div>
              )}

              {/* Gallery view — always shown on mobile; shown on desktop when mode is gallery */}
              <div className={viewMode === 'list' ? 'md:hidden' : 'block'}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {displayedTemplates.map((template) => (
                    <TemplateCard
                      key={template.id}
                      template={template}
                      onSelect={(t) => navigate(`/templates/${t.id}`)}
                      onDelete={handleDeleteTemplate}
                      onEvaluate={handleEvaluateTemplate}
                      selectable={true}
                      selected={selectedIds.has(template.id)}
                      onToggleSelect={handleSelectOne}
                      canEditVisibility={canArchive}
                      canArchive={canArchive}
                    />
                  ))}
                </div>
              </div>
            </>
          )}
        </>
      ) : (
        <div className="text-center py-12">
          <p className="text-text-muted mb-4">No templates yet</p>
          <Button
            onClick={() => navigate('/templates/create')}
            className="bg-accent hover:bg-accent-hover text-dark-base font-semibold"
          >
            Create your first template
          </Button>
        </div>
      )}

      {/* Single-delete Confirmation Modal */}
      <ConfirmationDialog
        isOpen={templateToDelete !== null}
        onClose={() => setTemplateToDelete(null)}
        onConfirm={confirmDeleteTemplate}
        title="Delete Template"
        description="Are you sure you want to delete this template? This action cannot be undone."
        confirmLabel="Delete"
        variant="warning"
      />

      {/* Delete Selected Confirmation Modal */}
      <ConfirmationDialog
        isOpen={showDeleteSelectedModal}
        onClose={() => setShowDeleteSelectedModal(false)}
        onConfirm={handleDeleteSelected}
        title="Delete Selected Templates"
        description={`Are you sure you want to delete ${selectedIds.size} template(s)? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="warning"
        isLoading={bulkDelete.isPending}
      />

      {/* Delete All Confirmation Modal */}
      <ConfirmationDialog
        isOpen={showDeleteAllModal}
        onClose={() => setShowDeleteAllModal(false)}
        onConfirm={handleDeleteAll}
        title="Delete All Templates"
        description={`This will permanently delete ALL ${templates?.length ?? 0} templates. This action cannot be undone.`}
        confirmLabel="Delete All"
        variant="danger"
        isLoading={deleteAll.isPending}
      />
    </div>
  );
}

export default TemplateLibrary;
